import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '../../../components/ui/Button.js';
import { Input, FormField } from '../../../components/ui/FormField.js';
import { Money } from '../../../components/ui/Money';
import { Search, Printer, FileDown, Calendar } from 'lucide-react';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useDaybook } from '../../../features/reports/client';
import { TableSkeleton } from '../../../components/ui/Skeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { useToast } from '../../../components/ui/Toast';
import { useRunReport, useReportRun, useDownloadRun } from '../../../features/reporting/hooks';

export const Route = createFileRoute('/_auth/reports/daybook')({
 component: DaybookReportPage,
});

function DaybookReportPage() {
  const [date, setDate] = React.useState(new Date().toISOString().split('T')[0]);
  const [query, setQuery] = React.useState('');
  const { data: entries = [], isLoading, isError, error, refetch } = useDaybook(date);

  const { toast } = useToast();
  const [activeRunId, setActiveRunId] = React.useState<string | null>(null);
  const runMutation = useRunReport();
  const downloadMutation = useDownloadRun();
  const { data: runData } = useReportRun(activeRunId ?? '', { enabled: !!activeRunId });

  React.useEffect(() => {
    if (runData?.status === 'COMPLETED' && activeRunId) {
      downloadMutation.mutate(activeRunId, {
        onSuccess: () => setActiveRunId(null),
        onError: () => setActiveRunId(null),
      });
    } else if (runData?.status === 'FAILED') {
      setActiveRunId(null);
      toast({ type: 'error', title: 'Export Failed', description: 'Failed to generate report PDF.' });
    }
  }, [runData, activeRunId, toast]);

  const handleExportPdf = () => {
    setActiveRunId(null);
    runMutation.mutate({
      reportCode: 'DAYBOOK',
      parameters: { from_date: date, to_date: date },
      format: 'PDF',
    }, {
      onSuccess: (data) => {
        if (data?.id) setActiveRunId(data.id);
      },
      onError: (err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to start report generation';
        toast({ type: 'error', title: 'Export Error', description: message });
      },
    });
  };

  const filteredEntries = entries.filter(e =>
    e.accountName.toLowerCase().includes(query.toLowerCase()) ||
    e.voucherNumber.toLowerCase().includes(query.toLowerCase()) ||
    e.narration.toLowerCase().includes(query.toLowerCase())
  );

  const totalReceipts = filteredEntries.filter(e => e.entryType === 'RECEIPT').reduce((acc, e) => acc + e.amountPaise, 0);
  const totalPayments = filteredEntries.filter(e => e.entryType === 'PAYMENT').reduce((acc, e) => acc + e.amountPaise, 0);
  const netFlow = totalReceipts - totalPayments;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Daily Daybook"
        subtitle="Audited, sequential log of all cash, bank, and journal ledger transactions recorded during the day."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => window.print()}>
              <Printer className="w-4 h-4 mr-2" />
              Print Daybook
            </Button>
            <Button variant="primary" onClick={handleExportPdf} disabled={runMutation.isPending || (!!activeRunId && runData?.status !== 'FAILED')}>
              <FileDown className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        }
      />
      <div className="flex-1 w-full min-h-0">
        <div className="flex flex-wrap gap-4 items-center justify-between w-full mb-4">
          <div className="flex gap-4 items-center">
            <div className="flex flex-col gap-1">
              <label className="text-caption font-bold text-text-secondary uppercase tracking-wider">Report Date</label>
              <Input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="h-9 w-44"
              />
            </div>
            <FormField label="Filter Journal">
              <Input
                startIcon={<Search />}
                placeholder="Search account, voucher..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-64"
              />
            </FormField>
          </div>

          <div className="flex gap-6 font-mono text-xs font-semibold text-text-secondary pr-4">
            <div>Receipts: <span className="text-success-text"><Money paise={totalReceipts} /></span></div>
            <div>Payments: <span className="text-danger-600"><Money paise={totalPayments} /></span></div>
            <div>Net Flow: <span className={netFlow >= 0 ? 'text-success-text font-bold' : 'text-danger-600 font-bold'}><Money paise={netFlow} /></span></div>
          </div>
        </div>

        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          {isLoading ? (
            <TableSkeleton columns={7} rows={5} />
          ) : isError ? (
            <div className="p-8 text-center">
              <p className="text-sm font-semibold text-danger-600">Failed to load daybook</p>
              <p className="text-xs text-text-muted mt-1">{error?.message || 'Unknown error'}</p>
              <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
            </div>
          ) : filteredEntries.length === 0 ? (
            <EmptyState
              title="No transactions found"
              description={query ? 'No entries match your filter.' : `No transactions recorded for ${date}.`}
              icon={<Calendar className="w-8 h-8" />}
            />
          ) : (
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Voucher No.</th>
                  <th className="p-4">Account Head</th>
                  <th className="p-4">Entry Type</th>
                  <th className="p-4">Debited (Receipts)</th>
                  <th className="p-4">Credited (Payments)</th>
                  <th className="p-4">Narration / Memo</th>
                  <th className="p-4">Document Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {filteredEntries.map(e => (
                  <tr key={e.id} className="hover:bg-surface-2 transition-colors">
                    <td className="p-4 font-mono text-xs font-semibold">{e.voucherNumber}</td>
                    <td className="p-4 font-bold">{e.accountName}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full border ${
                        e.entryType === 'RECEIPT'
                          ? 'text-success-text bg-success-bg border-success-bg'
                          : 'text-danger-text bg-danger-bg border-danger-bg'
                      }`}>
                        {e.entryType === 'RECEIPT' ? '↑' : '↓'}
                        {e.entryType}
                      </span>
                    </td>
                    <td className="p-4 font-semibold text-success-text">
                      {e.entryType === 'RECEIPT' ? <Money paise={e.amountPaise} /> : '—'}
                    </td>
                    <td className="p-4 font-semibold text-danger-600">
                      {e.entryType === 'PAYMENT' ? <Money paise={e.amountPaise} /> : '—'}
                    </td>
                    <td className="p-4 text-xs text-text-secondary max-w-xs">{e.narration}</td>
                    <td className="p-4 font-mono text-xs text-text-muted">{e.reference}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
